import { useEffect, useState } from 'react';
import { Trans } from 'react-i18next';
import { connect, type ConnectedProps } from 'react-redux';
import {
  CharacterTextInput,
  IconValidationInvalid,
  InformationalScreen,
  LoadingModal,
  Stack,
  Text,
} from '@adc/parallax-component-library';
import {
  Button,
  Footer,
  FormContainer,
  MainContainer,
  TopHeaderBackArrow,
} from 'Components/utility';
import { ApiStatus, StorageKeys } from 'Constants';
import { useFormik } from 'formik';
import { type RootState } from 'Reducers/index';
import { forgotPassword, verifyPhoneCode } from 'Services/verify';
import { notifyApp } from 'Utilities/appCommunicator';
import i18n from 'Utilities/i18n';
import mediator from 'Utilities/mediator';
import * as z from 'zod';
import { toFormikValidationSchema } from 'zod-formik-adapter';

const mapStateToProps = ({ env: { deviceId, osType, osVersion, app, appVersion } }: RootState) => ({
  deviceId,
  osType,
  osVersion,
  app,
  appVersion,
});

const connector = connect(mapStateToProps);

type Props = ConnectedProps<typeof connector>;

const VERIFICATION_CODE_LENGTH = 6;

const VerifyPhone = ({ deviceId, osType, osVersion, app, appVersion }: Props) => {
  const validationSchema = z.object({
    code: z
      .string(i18n.t('VerifyPhone.content.verifyPhoneForm.formField.code.errors.required'))
      .min(
        VERIFICATION_CODE_LENGTH,
        i18n.t('VerifyPhone.content.verifyPhoneForm.formField.code.errors.required')
      )
      .regex(
        /^[0-9]+$/,
        i18n.t('VerifyPhone.content.verifyPhoneForm.formField.code.errors.codeValid')
      ),
  });

  const [isResendingCode, setIsResendingCode] = useState(false);

  const [phone] = useState(() => sessionStorage.getItem(StorageKeys.FORGOT_PASSWORD_PHONE));

  useEffect(() => {
    if (!phone) {
      mediator.publish('router:redirect', '/phone-forgot-password');
    }
  }, [phone]);

  const {
    handleSubmit,
    handleChange,
    handleBlur,
    isValid,
    dirty,
    errors,
    touched,
    values,
    isSubmitting,
  } = useFormik({
    initialValues: {
      code: '',
    },
    validationSchema: toFormikValidationSchema(validationSchema),
    validateOnBlur: true,
    onSubmit: async ({ code }, { setFieldError }) => {
      if (!phone) {
        return;
      }

      const { data } = await verifyPhoneCode(
        code,
        phone,
        osType,
        osVersion,
        app,
        appVersion,
        deviceId
      );

      if (data?.code === ApiStatus.PHONE_CODE_INVALID) {
        notifyApp('adc-webview:verify-phone', { status: data.code, data });

        setFieldError(
          'code',
          i18n.t('VerifyPhone.content.verifyPhoneForm.formField.code.errors.invalid')
        );

        return;
      }

      notifyApp('adc-webview:verify-phone', data);

      mediator.publish('router:navigate', '/password-setup?reset=true');
    },
  });

  const handleResendCode = async () => {
    if (!phone) {
      return;
    }

    setIsResendingCode(true);

    try {
      await forgotPassword(phone, osType, osVersion, app, appVersion, deviceId);
    } finally {
      setIsResendingCode(false);
    }
  };

  const handleNavigateBack = () => {
    mediator.publish('router:back');
    notifyApp('adc-webview:navigate-back-app');
  };

  const codeHasError = Boolean(errors.code && touched.code);

  return (
    <MainContainer>
      {(isSubmitting || isResendingCode) && <LoadingModal aria-label="Loading" />}

      <InformationalScreen
        topNavigationProps={{
          animatedTitle: true,
          title: i18n.t('VerifyPhone.title'),
          buttonStart: {
            element: <TopHeaderBackArrow testID="Auth.action-go-back" />,
            label: i18n.t('VerifyPhone.title'),
            onPress: handleNavigateBack,
          },
          testID: 'Auth.actionVerifyPhoneTitle',
        }}
      >
        <InformationalScreen.Page>
          <InformationalScreen.Content>
            <Text marginTop={-8}>
              <Trans
                i18n={i18n}
                i18nKey="VerifyPhone.subtitle"
                values={{ phone: phone?.slice(-4) }}
                components={{
                  Bold: <Text fontWeight="bold" />,
                }}
              />
            </Text>
            <Stack marginTop="$4" gap="$4">
              <Text>{i18n.t('VerifyPhone.content.action')}</Text>

              <FormContainer hasHiddenUserInput>
                <CharacterTextInput
                  length={VERIFICATION_CODE_LENGTH}
                  testID="VerifyPhone.code"
                  label={i18n.t('VerifyPhone.content.verifyPhoneForm.formField.code.label')}
                  value={values.code}
                  onValueChange={handleChange('code')}
                  error={codeHasError}
                  supportingIconConfig={
                    codeHasError ? { icon: <IconValidationInvalid /> } : undefined
                  }
                  supportingText={codeHasError ? errors.code : undefined}
                  // @ts-expect-error Parallax doesn't type "onBlur" well, but accepts it
                  onBlur={handleBlur('code')}
                />
              </FormContainer>
            </Stack>

            <Stack marginTop="$8" gap="$2">
              <Text textAlign="center">
                {i18n.t('VerifyPhone.content.verifyPhoneForm.resendCode.label')}
              </Text>

              <Button
                variant="tertiary"
                label={i18n.t('VerifyPhone.content.verifyPhoneForm.links.resend.label')}
                onPress={handleResendCode}
                testID="VerifyPhone.resend-code-btn"
                disabled={isResendingCode}
              />
            </Stack>
          </InformationalScreen.Content>

          <InformationalScreen.Footer>
            <Footer
              buttonLabel={i18n.t('VerifyPhone.content.verifyPhoneForm.links.next.label')}
              onButtonPress={handleSubmit}
              buttonTestID="VerifyPhone.submit-btn"
              disabled={!isValid || !dirty}
            />
          </InformationalScreen.Footer>
        </InformationalScreen.Page>
      </InformationalScreen>
    </MainContainer>
  );
};

export default connector(VerifyPhone);
